import React, { useCallback, useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import { API_BASE_URL } from '../App';
import {
  clearJurisdictionsCatalogCache,
  useJurisdictionsCatalog,
} from '../lib/jurisdictionsCatalog';

const ADMIN_EMAIL = 'joseph.e.combs@gmail.com';

/**
 * Admin CMS for jurisdictions / scopes (isAdmin email only).
 * @param {{ user: { email?: string } | null }} props
 */
function AdminJurisdictionsPage({ user }) {
  const sessionId = Cookies.get('session');
  const { refreshCatalog } = useJurisdictionsCatalog();
  const [scopes, setScopes] = useState([]);
  const [rows, setRows] = useState([]);
  const [recovered, setRecovered] = useState([]);
  const [claimEmailById, setClaimEmailById] = useState({});
  const [forceClaimById, setForceClaimById] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [newRow, setNewRow] = useState({
    id: '',
    scopeCode: 'US',
    name: '',
    alphaCode: '',
    geoMatchName: '',
  });

  const isAdminUser = user?.email === ADMIN_EMAIL;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      clearJurisdictionsCatalogCache();
      const res = await fetch(`${API_BASE_URL}/admin/jurisdictions`, {
        headers: { Authorization: sessionId || '' },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Admin load failed (${res.status})`);
      }
      const catalog = await res.json();
      setScopes(
        (catalog.scopes || []).map((s) => ({
          code: s.code,
          label: s.label,
          flag: s.flag,
          regionsLabel: s.regions_label || s.regionsLabel,
          displayOrder: s.display_order ?? s.displayOrder,
          isCombined: Boolean(s.is_combined ?? s.isCombined),
          active: s.active !== 0 && s.active !== false,
        })),
      );
      setRows(
        (catalog.jurisdictions || []).map((j) => ({
          id: j.id,
          scopeCode: j.scope_code || j.scopeCode,
          name: j.name,
          alphaCode: j.alpha_code || j.alphaCode,
          geoMatchName: j.geo_match_name || j.geoMatchName,
          active: j.active !== 0 && j.active !== false,
          draftName: j.name,
        })),
      );

      const recoveredRes = await fetch(`${API_BASE_URL}/admin/recovered-users`, {
        headers: { Authorization: sessionId || '' },
      });
      if (recoveredRes.ok) {
        const body = await recoveredRes.json();
        setRecovered(body.users || []);
      } else {
        setRecovered([]);
      }

      if (typeof refreshCatalog === 'function') await refreshCatalog();
    } catch (err) {
      setError(err.message || 'Failed to load jurisdictions');
    } finally {
      setLoading(false);
    }
  }, [refreshCatalog, sessionId]);

  const claimRecovered = async (recoveredUserId) => {
    const targetEmail = (claimEmailById[recoveredUserId] || '').trim();
    if (!targetEmail) {
      setError('Enter the live account email to claim into');
      return;
    }
    setBusyId(`recovered:${recoveredUserId}`);
    setStatus(null);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/admin/reconcile-recovered`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          recoveredUserId,
          targetEmail,
          force: Boolean(forceClaimById[recoveredUserId]),
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `Claim failed (${res.status})`);
      setStatus(
        `Claimed ${body.recoveredName || recoveredUserId}: moved ${body.transferred ?? '?'} sightings to ${targetEmail}`,
      );
      await load();
    } catch (err) {
      setError(err.message || 'Claim failed');
    } finally {
      setBusyId(null);
    }
  };
  useEffect(() => {
    if (isAdminUser) load();
    else setLoading(false);
  }, [isAdminUser, load]);

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: sessionId,
  });

  const patchJurisdiction = async (id, patch) => {
    setBusyId(id);
    setStatus(null);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/admin/jurisdictions/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify(patch),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `PATCH failed (${res.status})`);
      }
      const updated = await res.json().catch(() => null);
      setRows((prev) =>
        prev.map((r) => {
          if (r.id !== id) return r;
          const name = updated?.name ?? patch.name ?? r.name;
          const active =
            updated?.active != null
              ? Boolean(updated.active)
              : patch.active != null
                ? Boolean(patch.active)
                : r.active;
          return {
            ...r,
            ...patch,
            name,
            draftName: name,
            active,
            geoMatchName: updated?.geo_match_name || updated?.geoMatchName || r.geoMatchName,
          };
        }),
      );
      setStatus(`Saved jurisdiction ${id}`);
      clearJurisdictionsCatalogCache();
      if (typeof refreshCatalog === 'function') await refreshCatalog();
    } catch (err) {
      setError(err.message || 'Save failed');
    } finally {
      setBusyId(null);
    }
  };

  const patchScope = async (code, patch) => {
    setBusyId(`scope:${code}`);
    setStatus(null);
    try {
      const res = await fetch(`${API_BASE_URL}/admin/scopes/${encodeURIComponent(code)}`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify(patch),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `PATCH scope failed (${res.status})`);
      }
      setStatus(`Saved scope ${code}`);
      await load();
    } catch (err) {
      setError(err.message || 'Scope save failed');
    } finally {
      setBusyId(null);
    }
  };

  const createJurisdiction = async (e) => {
    e.preventDefault();
    setBusyId('create');
    setStatus(null);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/admin/jurisdictions`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          id: newRow.id.trim(),
          scopeCode: newRow.scopeCode,
          name: newRow.name.trim(),
          alphaCode: newRow.alphaCode.trim() || null,
          geoMatchName: newRow.geoMatchName.trim() || newRow.name.trim(),
          active: true,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Create failed (${res.status})`);
      }
      setNewRow({ id: '', scopeCode: 'US', name: '', alphaCode: '', geoMatchName: '' });
      setStatus('Created jurisdiction');
      await load();
    } catch (err) {
      setError(err.message || 'Create failed');
    } finally {
      setBusyId(null);
    }
  };

  if (!user || !isAdminUser) {
    return (
      <div className="privacy-policy">
        <h1>Jurisdictions</h1>
        <section>
          <h2>Access Denied</h2>
          <p>Admin access is required to manage jurisdictions.</p>
        </section>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="privacy-policy">
        <h1>Jurisdictions</h1>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <div style={{ fontSize: '1.2rem', color: '#4285f4' }}>Loading catalog…</div>
        </div>
      </div>
    );
  }

  return (
    <div className="privacy-policy admin-jurisdictions">
      <h1>Jurisdictions CMS</h1>
      <p style={{ color: '#666', fontSize: '0.9rem' }}>
        Rename regions, toggle active, and edit scope labels. Session cookie required.
      </p>
      {status && (
        <p style={{ color: '#2E7D32', fontWeight: 600 }} role="status">
          {status}
        </p>
      )}
      {error && (
        <p style={{ color: '#c62828' }} role="alert">
          {error}
        </p>
      )}

      <section>
        <h2>Recovered players (claim by NAME)</h2>
        <p style={{ color: '#666', fontSize: '0.9rem' }}>
          Auto-reconcile matches the rejoining player&apos;s <strong>display name</strong> to a
          recovered name (not email). Ambiguous names need a manual claim below — enter the live
          account email only to identify who already signed up; names must still match unless you
          check force.
        </p>
        {recovered.length === 0 ? (
          <p style={{ color: '#2E7D32' }}>No unreclaimed placeholders.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="admin-jurisdictions__table">
              <thead>
                <tr>
                  <th>Recovered name</th>
                  <th>Restored email / placeholder</th>
                  <th>Sightings</th>
                  <th>Live account email</th>
                  <th>Force if names differ</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {recovered.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <strong>{r.name || '—'}</strong>
                      {r.nameNorm ? (
                        <div style={{ fontSize: '0.75rem', color: '#888' }}>{r.nameNorm}</div>
                      ) : null}
                    </td>
                    <td>
                      <code style={{ fontSize: '0.75rem' }}>{r.email}</code>
                    </td>
                    <td>{r.sightingCount}</td>
                    <td>
                      <input
                        type="email"
                        placeholder="signed-up player@…"
                        value={claimEmailById[r.id] || ''}
                        onChange={(e) =>
                          setClaimEmailById((prev) => ({ ...prev, [r.id]: e.target.value }))
                        }
                      />
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        checked={Boolean(forceClaimById[r.id])}
                        onChange={(e) =>
                          setForceClaimById((prev) => ({ ...prev, [r.id]: e.target.checked }))
                        }
                        aria-label={`Force claim for ${r.name || r.id}`}
                      />
                    </td>
                    <td>
                      <button
                        type="button"
                        className="admin-jurisdictions__btn"
                        disabled={busyId === `recovered:${r.id}`}
                        onClick={() => claimRecovered(r.id)}
                      >
                        Claim by name
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section>
        <h2>Scopes</h2>
        <div style={{ overflowX: 'auto' }}>
          <table className="admin-jurisdictions__table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Label</th>
                <th>Flag</th>
                <th>Regions label</th>
                <th>Order</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {scopes.map((scope) => (
                <tr key={scope.code}>
                  <td>
                    <code>{scope.code}</code>
                  </td>
                  <td>
                    <input
                      type="text"
                      value={scope.label || ''}
                      onChange={(e) => {
                        const label = e.target.value;
                        setScopes((prev) =>
                          prev.map((s) => (s.code === scope.code ? { ...s, label } : s)),
                        );
                      }}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={scope.flag || ''}
                      style={{ width: '4rem' }}
                      onChange={(e) => {
                        const flag = e.target.value;
                        setScopes((prev) =>
                          prev.map((s) => (s.code === scope.code ? { ...s, flag } : s)),
                        );
                      }}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={scope.regionsLabel || ''}
                      onChange={(e) => {
                        const regionsLabel = e.target.value;
                        setScopes((prev) =>
                          prev.map((s) => (s.code === scope.code ? { ...s, regionsLabel } : s)),
                        );
                      }}
                    />
                  </td>
                  <td>{scope.displayOrder}</td>
                  <td>
                    <button
                      type="button"
                      className="admin-jurisdictions__btn"
                      disabled={busyId === `scope:${scope.code}`}
                      onClick={() =>
                        patchScope(scope.code, {
                          label: scope.label,
                          flag: scope.flag,
                          regionsLabel: scope.regionsLabel,
                        })
                      }
                    >
                      Save
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h2>Jurisdictions</h2>
        <div style={{ overflowX: 'auto' }}>
          <table className="admin-jurisdictions__table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Scope</th>
                <th>Name</th>
                <th>Alpha</th>
                <th>Geo match</th>
                <th>Active</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <code>{row.id}</code>
                  </td>
                  <td>{row.scopeCode}</td>
                  <td>
                    <input
                      type="text"
                      value={row.draftName}
                      onChange={(e) => {
                        const draftName = e.target.value;
                        setRows((prev) =>
                          prev.map((r) => (r.id === row.id ? { ...r, draftName } : r)),
                        );
                      }}
                    />
                  </td>
                  <td>{row.alphaCode || '—'}</td>
                  <td style={{ fontSize: '0.85rem' }}>{row.geoMatchName || '—'}</td>
                  <td>
                    <input
                      type="checkbox"
                      checked={Boolean(row.active)}
                      disabled={busyId === row.id}
                      onChange={(e) => patchJurisdiction(row.id, { active: e.target.checked })}
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="admin-jurisdictions__btn"
                      disabled={busyId === row.id || row.draftName === row.name}
                      onClick={() => patchJurisdiction(row.id, { name: row.draftName })}
                    >
                      Rename
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h2>Add jurisdiction</h2>
        <form className="admin-jurisdictions__form" onSubmit={createJurisdiction}>
          <label>
            ID
            <input
              required
              value={newRow.id}
              onChange={(e) => setNewRow((r) => ({ ...r, id: e.target.value }))}
            />
          </label>
          <label>
            Scope
            <select
              value={newRow.scopeCode}
              onChange={(e) => setNewRow((r) => ({ ...r, scopeCode: e.target.value }))}
            >
              {scopes
                .filter((s) => !s.isCombined)
                .map((s) => (
                  <option key={s.code} value={s.code}>
                    {s.code}
                  </option>
                ))}
            </select>
          </label>
          <label>
            Name
            <input
              required
              value={newRow.name}
              onChange={(e) => setNewRow((r) => ({ ...r, name: e.target.value }))}
            />
          </label>
          <label>
            Alpha
            <input
              value={newRow.alphaCode}
              onChange={(e) => setNewRow((r) => ({ ...r, alphaCode: e.target.value }))}
            />
          </label>
          <label>
            Geo match name
            <input
              value={newRow.geoMatchName}
              placeholder="defaults to name"
              onChange={(e) => setNewRow((r) => ({ ...r, geoMatchName: e.target.value }))}
            />
          </label>
          <button type="submit" className="admin-jurisdictions__btn" disabled={busyId === 'create'}>
            Create
          </button>
        </form>
      </section>
    </div>
  );
}

export default AdminJurisdictionsPage;
